import { DateFormats, formatDate } from '@strata/helper-functions';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { DollarInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { updateClaimStatus } from 'api/medbillingAPI';
import { UpdateClaimStatusServices } from 'components/claims/dialogs/UpdateClaimStatusServices';
import { AdditionalFields } from 'components/shared/AdditionalFields';
import { handleError } from 'services/Error';
import {
    useGetCategoryCodesQuery,
    useGetClaimDetailsQuery,
    useGetStatusCodesQuery,
    useRefetchAllClaimDetails,
} from 'store/api/medbillingApi';

const Title = 'Update Claim Status';

const ReasonOptions = ['Confirmed with Payer', 'Payer Error', 'Payer Processing Issue'];

const ValidationSchema = Yup.object({
    payerClaimNumber: Yup.string().required(validationStrings.requiredField.errorMessage),
    submittedAmount: Yup.number().required(validationStrings.requiredField.errorMessage),
    claimTotal: Yup.number().required(validationStrings.requiredField.errorMessage),
    paidDate: DateInputFormik.validator(),
    claimCategoryCode: Yup.string().required(validationStrings.requiredField.errorMessage),
    claimStatusCode: Yup.string().required(validationStrings.requiredField.errorMessage),
    checkNumber: Yup.string(),
    entityIdentifierCode: Yup.string(),
    secondaryClaimCategoryCode: Yup.string(),
    secondaryClaimStatusCode: Yup.string(),
    ...UpdateClaimStatusServices.validationSchema,
    ...ReasonInput.validationSchema,
});

interface UpdateClaimStatusDialogProps {
    onClose: () => void;
    payerId: string;
    transactionId: string;
}

export const UpdateClaimStatusDialog = ({
    onClose,
    payerId,
    transactionId,
}: UpdateClaimStatusDialogProps) => {
    const { data: claimDetails } = useGetClaimDetailsQuery({
        payerId,
        transactionId,
    });

    const { data: categoryCodes, isLoading: categoryCodesLoading } = useGetCategoryCodesQuery({
        payerId,
    });
    const { data: statusCodes, isLoading: statusCodesLoading } = useGetStatusCodesQuery({
        payerId,
    });

    const refetchAllClaimDetails = useRefetchAllClaimDetails({
        payerId,
        transactionId,
    });

    return (
        <Formik
            initialValues={
                {
                    ...claimDetails,
                    ...UpdateClaimStatusServices.initialValues(claimDetails?.services),
                } as Yup.InferType<typeof ValidationSchema>
            }
            enableReinitialize
            validateOnMount
            validationSchema={ValidationSchema}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await updateClaimStatus({
                        transactionId,
                        payerId,
                        claim: {
                            payer_claim_number: values.payerClaimNumber,
                            submitted_amount: values.submittedAmount,
                            paid_amount: values.claimTotal,
                            paid_date: formatDate(values.paidDate, DateFormats.HYPHEN_DATE),
                            claim_category_code: values.claimCategoryCode,
                            claim_status_code: values.claimStatusCode,
                            check_number: values.checkNumber,
                            entity_identifier_code: values.entityIdentifierCode,
                            secondary_claim_category_code: values.secondaryClaimCategoryCode,
                            secondary_claim_status_code: values.secondaryClaimStatusCode,
                            ...UpdateClaimStatusServices.submitValues(values),
                        },
                        ...ReasonInput.submitValues(values),
                    });

                    refetchAllClaimDetails();

                    SuccessToast({ message: 'Claim status updated.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update claim status. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Status"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <div className="sds_update-claim-dialog">
                            <div className="padding__bottom--medium">
                                <CollapsibleSection
                                    title="Claim Data"
                                    renderExpanded={false}
                                    content={
                                        <>
                                            <TextInputFormik
                                                id="payerClaimNumber"
                                                label="Payer Claim Number"
                                                required
                                            />
                                            <DollarInputFormik
                                                id="submittedAmount"
                                                label="Submitted Amount"
                                                required
                                            />
                                            <DollarInputFormik
                                                id="claimTotal"
                                                label="Paid Amount"
                                                required
                                            />
                                            <DateInputFormik id="paidDate" label="Paid Date" />
                                            <SuggestSingleSelectFormik
                                                id="claimCategoryCode"
                                                label="Claim Category Code"
                                                required
                                                values={categoryCodes}
                                                isLoading={categoryCodesLoading}
                                            />
                                            <SuggestSingleSelectFormik
                                                id="claimStatusCode"
                                                label="Claim Status Code"
                                                required
                                                values={statusCodes}
                                                isLoading={statusCodesLoading}
                                            />
                                            <TextInputFormik
                                                id="checkNumber"
                                                label="Check Number"
                                            />
                                            <AdditionalFields title="Claim Data">
                                                <TextInputFormik
                                                    id="entityIdentifierCode"
                                                    label="Entity Identifier Code"
                                                />
                                                <SuggestSingleSelectFormik
                                                    id="secondaryClaimCategoryCode"
                                                    label="Secondary Claim Category Code"
                                                    values={categoryCodes}
                                                    isLoading={categoryCodesLoading}
                                                />
                                                <SuggestSingleSelectFormik
                                                    id="secondaryClaimStatusCode"
                                                    label="Secondary Claim Status Code"
                                                    values={statusCodes}
                                                    isLoading={statusCodesLoading}
                                                />
                                            </AdditionalFields>
                                        </>
                                    }
                                />
                            </div>
                            <UpdateClaimStatusServices payerId={payerId} />
                            <ReasonInput reasons={ReasonOptions} />
                        </div>
                    }
                />
            )}
        </Formik>
    );
};

UpdateClaimStatusDialog.Title = Title;
